const anim = (kind, durationMs, easing = 'easeOut', intensity = 1, delayMs = 0) => ({ kind, durationMs, delayMs, easing, intensity });
/** Sensible defaults; each template overrides what makes it distinct. */
function base(partial) {
    return {
        version: 1,
        // Forward every top-level field the caller set (renderMode, ai, …); the
        // merged style/placement/segmentation below intentionally override the raw
        // partials so defaults are always filled in.
        ...partial,
        style: {
            fontFamily: 'Inter',
            fontWeight: 800,
            fontSizePx: 76,
            textTransform: 'none',
            lineHeight: 1.15,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#ffffff',
            background: { mode: 'none', radiusPx: 16, paddingXPx: 24, paddingYPx: 14 },
            activeWord: {
                color: '#ffd400',
                scale: 1,
                karaoke: false,
                animation: anim('pop', 220, 'spring', 1),
            },
            // Reveal-as-spoken (words appear one at a time) is the default look; a
            // template can set revealMode:'segment' to opt out.
            revealMode: 'word',
            entrance: anim('fade', 160, 'easeOut', 1),
            exit: anim('fade', 120, 'easeOut', 1),
            shadow: { color: 'rgba(0,0,0,0.55)', blurPx: 10, offsetXPx: 0, offsetYPx: 3 },
            ...(partial.style ?? {}),
            // Key word drops to its OWN next line, bigger, same color (no recolor).
            // Deep-merged AFTER the spread so the next-line look is the DEFAULT for
            // every template (a template inherits newLine/scale unless it sets its own).
            emphasis: { color: '#ffd400', scale: 1.5, bold: true, newLine: true, ...(partial.style?.emphasis ?? {}) },
        },
        placement: {
            anchor: 'lower',
            xNorm: 0.5,
            yNorm: 0,
            maxWidthNorm: 0.86,
            ...(partial.placement ?? {}),
        },
        segmentation: {
            maxWords: 6,
            preferredWords: 4,
            maxChars: 42,
            maxLines: 2,
            pauseThresholdMs: 500,
            breakOnSentencePunctuation: true,
            ...(partial.segmentation ?? {}),
        },
        id: partial.id,
        name: partial.name,
        category: partial.category,
        description: partial.description,
    };
}
export const TEMPLATES = [
    // Bold Pop #1 - premium default for talking-head Reels/Shorts.
    // Normal: white Inter SemiBold with a subtle shadow. Hook (the AI-detected
    // keyword, one per caption): yellow Inter ExtraBold on a dark rounded pill
    // with a quick pop. Hook grabs attention → normal text delivers the message.
    base({
        id: 'bold-pop-1',
        name: 'Bold Pop #1',
        category: 'pop',
        ai: true,
        description: 'Premium talking-head style - white SemiBold body, yellow ExtraBold hook on a dark pill (the AI keyword), fast restrained pops.',
        style: {
            fontFamily: 'Inter',
            fontWeight: 600, // SemiBold normals
            fontSizePx: 74,
            textTransform: 'none', // sentence case, never auto-uppercase
            lineHeight: 1.5,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#ffffff',
            // Subtle black shadow for readability over any footage.
            shadow: { color: 'rgba(0,0,0,0.5)', blurPx: 8, offsetXPx: 0, offsetYPx: 3 },
            background: { mode: 'none', radiusPx: 18, paddingXPx: 22, paddingYPx: 12 },
            // Spoken word gets a small, restrained pop.
            activeWord: {
                color: '#ffd400',
                scale: 1.05,
                karaoke: false,
                animation: anim('pop', 200, 'spring', 0.9),
            },
            // Key word on its own next line, bigger, SAME white color (size + weight
            // carry the emphasis - the signature clean short-form look).
            emphasis: { color: '#ffffff', scale: 1.5, bold: true, newLine: true },
            // Words appear one at a time as they're spoken (reveal-as-spoken by default).
            revealMode: 'word',
            entrance: anim('slideUp', 200, 'easeOut', 1),
            exit: anim('fade', 120, 'easeOut', 1),
        },
        placement: { anchor: 'bottom', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.82 },
        segmentation: {
            maxWords: 5,
            preferredWords: 3,
            maxChars: 32,
            maxLines: 2,
            pauseThresholdMs: 450,
            breakOnSentencePunctuation: true,
        },
    }),
    // Clean - simple, modern, highly readable with a soft shadow. The safe
    // default when the footage should lead. Key word: same white, just bigger.
    base({
        id: 'clean',
        name: 'Clean',
        category: 'clean',
        description: 'Simple, modern, highly readable captions with a soft shadow.',
        style: {
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSizePx: 68,
            textTransform: 'none',
            lineHeight: 1.2,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#ffffff',
            spokenColor: '#ffffff',
            background: { mode: 'none', radiusPx: 12, paddingXPx: 20, paddingYPx: 12 },
            shadow: { color: 'rgba(0,0,0,0.5)', blurPx: 8, offsetXPx: 0, offsetYPx: 2 },
            activeWord: { color: '#ffffff', scale: 1, karaoke: false, animation: anim('fade', 140) },
            emphasis: { color: '#ffffff', scale: 1.5, bold: true, newLine: true },
            entrance: anim('fade', 150),
            exit: anim('fade', 120),
        },
    }),
    // ══════════════════════════════════════════════════════════════════════════
    //  Fancy set - distinct TYPEFACES (not just colour swaps): an editorial serif
    //  (Fraunces), a tall condensed display (Anton), a handwriting script
    //  (Caveat), and a rounded geometric (Poppins). Each pairs the face with a
    //  matching emphasis treatment so styles read as genuinely different looks.
    // ══════════════════════════════════════════════════════════════════════════
    // Editorial - elegant magazine serif, warm cream body, one gold key word.
    base({
        id: 'editorial',
        name: 'Editorial',
        category: 'editorial',
        description: 'Elegant magazine serif (Fraunces) - warm cream body with a single gold key word inline.',
        style: {
            fontFamily: 'Fraunces',
            fontWeight: 600,
            fontSizePx: 74,
            textTransform: 'none',
            lineHeight: 1.18,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#f6f1e7',
            shadow: { color: 'rgba(0,0,0,0.5)', blurPx: 12, offsetXPx: 0, offsetYPx: 3 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            activeWord: { color: '#f6f1e7', scale: 1, karaoke: false, animation: anim('fade', 150) },
            emphasis: { color: '#e5b567', scale: 1.32, bold: false, newLine: false },
            entrance: anim('fade', 180, 'easeOut', 1),
            exit: anim('fade', 130),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.82 },
        segmentation: { maxWords: 5, preferredWords: 4, maxChars: 34, maxLines: 2, pauseThresholdMs: 480, breakOnSentencePunctuation: true },
    }),
    // Vogue - big centered all-caps serif display, key word alone + gold.
    base({
        id: 'vogue',
        name: 'Vogue',
        category: 'editorial',
        description: 'Big centered all-caps serif (Fraunces) display - the key word drops onto its own line in gold.',
        style: {
            fontFamily: 'Fraunces',
            fontWeight: 600,
            fontSizePx: 92,
            textTransform: 'uppercase',
            lineHeight: 1.08,
            letterSpacingPx: 1,
            textAlign: 'center',
            color: '#ffffff',
            shadow: { color: 'rgba(0,0,0,0.5)', blurPx: 14, offsetXPx: 0, offsetYPx: 5 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            activeWord: { color: '#ffffff', scale: 1, karaoke: false, animation: anim('fade', 150) },
            emphasis: { color: '#e7c877', scale: 1.36, bold: false, newLine: true },
            entrance: anim('fade', 170, 'easeOut', 1),
            exit: anim('fade', 130),
        },
        placement: { anchor: 'center', xNorm: 0.5, yNorm: 0.5, maxWidthNorm: 0.88 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 26, maxLines: 2, pauseThresholdMs: 460, breakOnSentencePunctuation: true },
    }),
    // Impact - tall condensed display (Anton), white with a hard black outline,
    // yellow key word. The high-energy Hormozi/creator punch, with a real
    // condensed face instead of a squished sans.
    base({
        id: 'impact',
        name: 'Impact',
        category: 'bold',
        description: 'Tall condensed display (Anton) - white with a hard black outline, the key word pops big in yellow.',
        style: {
            fontFamily: 'Anton',
            fontWeight: 400,
            fontSizePx: 124,
            textTransform: 'uppercase',
            lineHeight: 1.0,
            letterSpacingPx: 0.5,
            textAlign: 'center',
            color: '#ffffff',
            stroke: { color: '#000000', widthPx: 9 },
            shadow: { color: 'rgba(0,0,0,0.5)', blurPx: 8, offsetXPx: 0, offsetYPx: 5 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('pop', 220, 'spring', 1.05),
            activeWord: { color: '#ffffff', scale: 1.04, karaoke: false, animation: anim('pop', 190, 'spring', 0.9) },
            emphasis: { color: '#ffd400', scale: 1.3, bold: false, newLine: true },
            entrance: anim('none', 0),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'center', xNorm: 0.5, yNorm: 0.5, maxWidthNorm: 0.92 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 24, maxLines: 2, pauseThresholdMs: 380, breakOnSentencePunctuation: true },
    }),
    // Stamp - one or two giant condensed words with a warm gradient fill. Big,
    // graphic, title-card energy.
    base({
        id: 'stamp',
        name: 'Stamp',
        category: 'kinetic',
        description: 'One or two giant condensed words (Anton) with a warm gold gradient fill - title-card energy.',
        style: {
            fontFamily: 'Anton',
            fontWeight: 400,
            fontSizePx: 138,
            textTransform: 'uppercase',
            lineHeight: 0.98,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#fde68a',
            gradient: { from: '#fff3c4', to: '#f59e0b', angleDeg: 90 },
            shadow: { color: 'rgba(0,0,0,0.55)', blurPx: 16, offsetXPx: 0, offsetYPx: 6 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('pop', 240, 'spring', 1.15),
            activeWord: { color: '#fff3c4', scale: 1.06, karaoke: false, animation: anim('pop', 220, 'spring', 1) },
            emphasis: { color: '#fde68a', scale: 1.18, bold: false, newLine: true },
            entrance: anim('none', 0),
            exit: anim('fade', 130),
        },
        placement: { anchor: 'center', xNorm: 0.5, yNorm: 0.5, maxWidthNorm: 0.94 },
        segmentation: { maxWords: 3, preferredWords: 2, maxChars: 18, maxLines: 2, pauseThresholdMs: 420, breakOnSentencePunctuation: true },
    }),
    // Handwritten - warm handwriting script (Caveat) for a personal, vlog feel.
    base({
        id: 'handwritten',
        name: 'Handwritten',
        category: 'minimal',
        description: 'Warm handwriting script (Caveat) - a personal, friendly vlog caption with a soft pink accent.',
        style: {
            fontFamily: 'Caveat',
            fontWeight: 700,
            fontSizePx: 104,
            textTransform: 'none',
            lineHeight: 1.05,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#fffdf7',
            shadow: { color: 'rgba(0,0,0,0.45)', blurPx: 12, offsetXPx: 0, offsetYPx: 4 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            activeWord: { color: '#fffdf7', scale: 1, karaoke: false, animation: anim('fade', 150) },
            emphasis: { color: '#ff8fab', scale: 1.24, bold: false, newLine: false },
            entrance: anim('fade', 170, 'easeOut', 1),
            exit: anim('fade', 130),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.82 },
        segmentation: { maxWords: 5, preferredWords: 4, maxChars: 32, maxLines: 2, pauseThresholdMs: 480, breakOnSentencePunctuation: true },
    }),
    // Script Accent - clean rounded body (Poppins) with the key word dropped onto
    // its own line in a gold SCRIPT face (emphasis.fontFamily). The "the best MOVE"
    // magazine look - a genuine two-typeface pairing.
    base({
        id: 'script-accent',
        name: 'Script Accent',
        category: 'editorial',
        ai: true,
        description: 'Rounded sans body (Poppins) with the key word in a gold handwriting script on its own line - a real two-font pairing.',
        style: {
            fontFamily: 'Poppins',
            fontWeight: 800,
            fontSizePx: 70,
            textTransform: 'none',
            lineHeight: 1.14,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#ffffff',
            shadow: { color: 'rgba(0,0,0,0.5)', blurPx: 10, offsetXPx: 0, offsetYPx: 3 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            activeWord: { color: '#ffffff', scale: 1, karaoke: false, animation: anim('fade', 150) },
            emphasis: { color: '#ffd36e', scale: 1.7, bold: false, newLine: true, fontFamily: 'Caveat' },
            entrance: anim('slideUp', 190, 'easeOut', 1),
            exit: anim('fade', 130),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.82 },
        segmentation: { maxWords: 5, preferredWords: 3, maxChars: 30, maxLines: 2, pauseThresholdMs: 460, breakOnSentencePunctuation: true },
    }),
    // Friendly - soft rounded geometric (Poppins), sky-blue key word. Approachable
    // explainer / lifestyle look.
    base({
        id: 'friendly',
        name: 'Friendly',
        category: 'clean',
        description: 'Soft rounded geometric (Poppins) - an approachable explainer caption with a sky-blue key word.',
        style: {
            fontFamily: 'Poppins',
            fontWeight: 800,
            fontSizePx: 68,
            textTransform: 'none',
            lineHeight: 1.18,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#ffffff',
            shadow: { color: 'rgba(0,0,0,0.45)', blurPx: 9, offsetXPx: 0, offsetYPx: 3 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            activeWord: { color: '#ffffff', scale: 1, karaoke: false, animation: anim('fade', 140) },
            emphasis: { color: '#38bdf8', scale: 1.28, bold: true, newLine: false },
            entrance: anim('slideUp', 180, 'easeOut', 0.9),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.84 },
        segmentation: { maxWords: 5, preferredWords: 4, maxChars: 34, maxLines: 2, pauseThresholdMs: 470, breakOnSentencePunctuation: true },
    }),
    // Neon - electric gradient fill + glow on a rounded heavy face. Music/hype.
    base({
        id: 'neon',
        name: 'Neon',
        category: 'neon',
        description: 'Electric violet→cyan gradient with a soft glow on a heavy rounded face - music and hype edits.',
        style: {
            fontFamily: 'Poppins',
            fontWeight: 900,
            fontSizePx: 84,
            textTransform: 'uppercase',
            lineHeight: 1.06,
            letterSpacingPx: 0.5,
            textAlign: 'center',
            color: '#c4b5fd',
            gradient: { from: '#a78bfa', to: '#22d3ee', angleDeg: 90 },
            shadow: { color: 'rgba(96,120,255,0.65)', blurPx: 26, offsetXPx: 0, offsetYPx: 0 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('pop', 200, 'spring', 1),
            activeWord: { color: '#e0f2fe', scale: 1.06, karaoke: false, animation: anim('pop', 200, 'spring', 1) },
            emphasis: { color: '#f0abfc', scale: 1.22, bold: false, newLine: true },
            entrance: anim('none', 0),
            exit: anim('fade', 130),
        },
        placement: { anchor: 'center', xNorm: 0.5, yNorm: 0.5, maxWidthNorm: 0.9 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 24, maxLines: 2, pauseThresholdMs: 420, breakOnSentencePunctuation: true },
    }),
    // Highlighter - dark text on a rolling yellow marker highlight (perWord),
    // rounded face. The tactile "marker" caption.
    base({
        id: 'highlighter',
        name: 'Highlighter',
        category: 'highlight',
        description: 'Dark rounded text on a yellow marker highlight behind every word - a tactile, punchy caption.',
        style: {
            fontFamily: 'Poppins',
            fontWeight: 800,
            fontSizePx: 62,
            textTransform: 'none',
            lineHeight: 1.32,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#0e1220',
            spokenColor: '#0e1220',
            shadow: { color: 'rgba(0,0,0,0)', blurPx: 0, offsetXPx: 0, offsetYPx: 0 },
            background: { mode: 'perWord', color: '#fde047', radiusPx: 8, paddingXPx: 14, paddingYPx: 6 },
            activeWord: { color: '#0e1220', scale: 1, highlightColor: '#a3e635', karaoke: false, animation: anim('fade', 130) },
            emphasis: { color: '#0e1220', scale: 1.18, bold: true, newLine: false },
            entrance: anim('fade', 150),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.8 },
        segmentation: { maxWords: 5, preferredWords: 3, maxChars: 30, maxLines: 2, pauseThresholdMs: 470, breakOnSentencePunctuation: true },
    }),
    // ══════════════════════════════════════════════════════════════════════════
    //  Modern set (2026) - the bold-condensed-caps + hard-stroke + one-colored-
    //  keyword look that dominates short-form now, plus the karaoke word-fill we
    //  already support but didn't ship on. See docs/caption-design-findings.md.
    //  The "Engaged Hook" headline look is these captions + a behind-subject hook
    //  (hook.behind), which the engine already composites via the person matte -
    //  no template flag needed.
    // ══════════════════════════════════════════════════════════════════════════
    // Impact Pro - the Hormozi karaoke body. Tall condensed Anton caps, hard black
    // outline, word-by-word reveal, and karaoke fill so each spoken word (and the
    // ones before it) carry the gold. The key word drops onto its own bigger line.
    base({
        id: 'impact-pro',
        name: 'Impact Pro',
        category: 'karaoke',
        ai: true,
        description: 'Hormozi-style karaoke - tall condensed Anton caps with a hard black outline; each word turns gold as it is spoken.',
        style: {
            fontFamily: 'Anton',
            fontWeight: 400,
            fontSizePx: 116,
            textTransform: 'uppercase',
            lineHeight: 1.02,
            letterSpacingPx: 0.5,
            textAlign: 'center',
            color: '#ffffff',
            stroke: { color: '#000000', widthPx: 9 },
            shadow: { color: 'rgba(0,0,0,0.5)', blurPx: 8, offsetXPx: 0, offsetYPx: 5 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('pop', 200, 'spring', 1),
            // Karaoke: the spoken word fills gold left-to-right and stays gold.
            activeWord: { color: '#ffd400', scale: 1.05, karaoke: true, animation: anim('pop', 180, 'spring', 0.9) },
            // Key word on its own bigger line (size carries it; karaoke owns the color).
            emphasis: { color: '#ffffff', scale: 1.32, bold: false, newLine: true },
            entrance: anim('none', 0),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.9 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 24, maxLines: 2, pauseThresholdMs: 380, breakOnSentencePunctuation: true },
    }),
    // Green Machine - heavy Montserrat Black caps, hard black outline, one
    // saturated-green key word (the second Hormozi accent colour we were missing).
    base({
        id: 'green-machine',
        name: 'Green Machine',
        category: 'bold',
        description: 'Heavy Montserrat Black caps with a hard black outline and a single saturated-green key word.',
        style: {
            fontFamily: 'Montserrat',
            fontWeight: 900,
            fontSizePx: 100,
            textTransform: 'uppercase',
            lineHeight: 1.08,
            letterSpacingPx: -0.5,
            textAlign: 'center',
            color: '#ffffff',
            stroke: { color: '#000000', widthPx: 8 },
            shadow: { color: 'rgba(0,0,0,0.5)', blurPx: 8, offsetXPx: 0, offsetYPx: 4 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('pop', 200, 'spring', 1),
            activeWord: { color: '#eafff0', scale: 1.06, karaoke: false, animation: anim('pop', 190, 'spring', 0.9) },
            // Inline green key word (recoloured in place, not a new line).
            emphasis: { color: '#39ff14', scale: 1.24, bold: true, newLine: false },
            entrance: anim('none', 0),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.9 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 26, maxLines: 2, pauseThresholdMs: 400, breakOnSentencePunctuation: true },
    }),
    // Clean Caps - a punchier modern default than Bold Pop: Inter ExtraBold caps
    // with a thin black outline (survives muted, compressed feeds), one gold key
    // word, word-by-word reveal. Familiar, but reads as "viral" not "editorial".
    base({
        id: 'clean-caps',
        name: 'Clean Caps',
        category: 'bold',
        ai: true,
        description: 'A punchy modern default - Inter ExtraBold caps with a thin black outline and a single gold key word, revealed word by word.',
        style: {
            fontFamily: 'Inter',
            fontWeight: 800,
            fontSizePx: 86,
            textTransform: 'uppercase',
            lineHeight: 1.12,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#ffffff',
            stroke: { color: '#000000', widthPx: 5 },
            shadow: { color: 'rgba(0,0,0,0.5)', blurPx: 10, offsetXPx: 0, offsetYPx: 3 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('pop', 190, 'spring', 0.9),
            activeWord: { color: '#ffd400', scale: 1.05, karaoke: false, animation: anim('pop', 180, 'spring', 0.9) },
            emphasis: { color: '#ffd400', scale: 1.2, bold: true, newLine: false },
            entrance: anim('none', 0),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'bottom', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.86 },
        segmentation: { maxWords: 5, preferredWords: 3, maxChars: 30, maxLines: 2, pauseThresholdMs: 420, breakOnSentencePunctuation: true },
    }),
    // ══════════════════════════════════════════════════════════════════════════
    //  Cute / playful set - bubbly rounded faces, pastel & candy pills, rainbow
    //  word colours, gradient + sticker looks, a bouncy word reveal, and a LOOPING
    //  active-word motion (wiggle / jelly / glow) that plays while each word is
    //  spoken. For fun/lifestyle/kids/vlog content. The motion can be turned off
    //  per project via the Edit Style "Animate spoken word" toggle.
    // ══════════════════════════════════════════════════════════════════════════
    // Bubble Pop - rounded Baloo words each on a soft pastel pill; the spoken word
    // gets a minty pill. Bouncy, friendly, sticker-book energy.
    base({
        id: 'bubble-pop',
        name: 'Bubble Pop',
        category: 'pop',
        description: 'Rounded Baloo words on soft pastel pills with a bouncy reveal - cute lifestyle, wholesome, kids.',
        style: {
            fontFamily: 'Baloo2',
            fontWeight: 800,
            fontSizePx: 78,
            textTransform: 'none',
            lineHeight: 1.5,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#33264a',
            shadow: { color: 'rgba(0,0,0,0)', blurPx: 0, offsetXPx: 0, offsetYPx: 0 },
            background: { mode: 'perWord', color: '#ffd0e6', radiusPx: 28, paddingXPx: 22, paddingYPx: 8 },
            revealMode: 'word',
            wordEntrance: anim('bounce', 320, 'spring', 1),
            activeWord: { color: '#33264a', scale: 1.06, highlightColor: '#bff3df', karaoke: false, animation: anim('wiggle', 620, 'linear', 1) },
            emphasis: { color: '#c0398a', scale: 1.18, bold: true, newLine: false },
            entrance: anim('none', 0),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.84 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 24, maxLines: 2, pauseThresholdMs: 440, breakOnSentencePunctuation: true },
    }),
    // Rainbow - each word a different rainbow colour (palette) in a rounded Fredoka
    // face with a white outline; the spoken word pops white. Playful and colourful.
    base({
        id: 'rainbow',
        name: 'Rainbow',
        category: 'kinetic',
        description: 'Rounded Fredoka with each word a different rainbow colour and a white outline - best for music, party, kids.',
        style: {
            fontFamily: 'Fredoka',
            fontWeight: 700,
            fontSizePx: 88,
            textTransform: 'uppercase',
            lineHeight: 1.15,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#ff5d8f',
            palette: ['#ff5d8f', '#ff9f45', '#ffd23f', '#3bd671', '#3aa0ff', '#a66bff'],
            stroke: { color: '#ffffff', widthPx: 6 },
            shadow: { color: 'rgba(0,0,0,0.18)', blurPx: 0, offsetXPx: 0, offsetYPx: 4 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('bounce', 320, 'spring', 1.05),
            activeWord: { color: '#ffffff', scale: 1.08, karaoke: false, animation: anim('jelly', 560, 'linear', 1) },
            emphasis: { color: '#ffffff', scale: 1.2, bold: false, newLine: false },
            entrance: anim('none', 0),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.9 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 24, maxLines: 2, pauseThresholdMs: 420, breakOnSentencePunctuation: true },
    }),
    // Jelly - chunky Luckiest Guy caps in bright colours with a dark outline and a
    // big bouncy pop. Fun, high-energy, cartoon feel.
    base({
        id: 'jelly',
        name: 'Jelly',
        category: 'kinetic',
        description: 'Chunky Luckiest Guy caps in bright colours with a dark outline and a big bouncy pop - comedy, gaming, goofy skits.',
        style: {
            fontFamily: 'LuckiestGuy',
            fontWeight: 400,
            fontSizePx: 96,
            textTransform: 'uppercase',
            lineHeight: 1.05,
            letterSpacingPx: 0.5,
            textAlign: 'center',
            color: '#ffffff',
            palette: ['#ff5d8f', '#3aa0ff', '#ffd23f', '#3bd671'],
            stroke: { color: '#17121f', widthPx: 8 },
            shadow: { color: 'rgba(0,0,0,0.35)', blurPx: 8, offsetXPx: 0, offsetYPx: 5 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('bounce', 340, 'spring', 1.15),
            activeWord: { color: '#ffffff', scale: 1.1, karaoke: false, animation: anim('jelly', 520, 'linear', 1.1) },
            emphasis: { color: '#ffffff', scale: 1.2, bold: false, newLine: false },
            entrance: anim('none', 0),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'center', xNorm: 0.5, yNorm: 0.5, maxWidthNorm: 0.92 },
        segmentation: { maxWords: 3, preferredWords: 2, maxChars: 20, maxLines: 2, pauseThresholdMs: 420, breakOnSentencePunctuation: true },
    }),
    // Candy Pop - heavy Nunito words on candy-coloured pills; the spoken word gets
    // a lilac pill. Sweet, bold, bouncy.
    base({
        id: 'candy-pop',
        name: 'Candy Pop',
        category: 'pop',
        description: 'Heavy Nunito words on candy-coloured pills with a bouncy reveal - food, dessert, sweet/ASMR.',
        style: {
            fontFamily: 'Nunito',
            fontWeight: 900,
            fontSizePx: 74,
            textTransform: 'none',
            lineHeight: 1.5,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#3a2b3f',
            shadow: { color: 'rgba(0,0,0,0)', blurPx: 0, offsetXPx: 0, offsetYPx: 0 },
            background: { mode: 'perWord', color: '#ffe0c7', radiusPx: 22, paddingXPx: 20, paddingYPx: 8 },
            revealMode: 'word',
            wordEntrance: anim('bounce', 320, 'spring', 1),
            activeWord: { color: '#3a2b3f', scale: 1.06, highlightColor: '#e0d4ff', karaoke: false, animation: anim('glow', 800, 'linear', 1) },
            emphasis: { color: '#c0398a', scale: 1.18, bold: true, newLine: false },
            entrance: anim('none', 0),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.84 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 24, maxLines: 2, pauseThresholdMs: 440, breakOnSentencePunctuation: true },
    }),
    // Pastel Float - soft Chewy script with a white→lilac gradient fill and a gentle
    // slide-up. Dreamy, calm, aesthetic.
    base({
        id: 'pastel-float',
        name: 'Pastel Float',
        category: 'minimal',
        description: 'Soft Chewy script with a white-to-lilac gradient fill and a gentle slide-up - aesthetic, calm, dreamy edits.',
        style: {
            fontFamily: 'Chewy',
            fontWeight: 400,
            fontSizePx: 92,
            textTransform: 'none',
            lineHeight: 1.1,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#ffd6f2',
            gradient: { from: '#ffffff', to: '#c8b6ff', angleDeg: 90 },
            shadow: { color: 'rgba(120,80,160,0.35)', blurPx: 14, offsetXPx: 0, offsetYPx: 2 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('slideUp', 220, 'easeOut', 0.9),
            activeWord: { color: '#ffffff', scale: 1.05, karaoke: false, animation: anim('wiggle', 700, 'linear', 0.8) },
            emphasis: { color: '#ffd6f2', scale: 1.2, bold: false, newLine: false },
            entrance: anim('none', 0),
            exit: anim('fade', 130),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.84 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 26, maxLines: 2, pauseThresholdMs: 460, breakOnSentencePunctuation: true },
    }),
    // Sticker - bright Baloo fill with a thick WHITE sticker outline and a hard drop
    // shadow, so each word looks like a peel-off sticker. Bouncy and graphic.
    base({
        id: 'sticker',
        name: 'Sticker',
        category: 'bold',
        description: 'Bright Baloo words with a thick white sticker outline and a hard drop shadow - fun, playful, meme energy.',
        style: {
            fontFamily: 'Baloo2',
            fontWeight: 800,
            fontSizePx: 82,
            textTransform: 'none',
            lineHeight: 1.2,
            letterSpacingPx: 0,
            textAlign: 'center',
            color: '#ff5d8f',
            palette: ['#ff5d8f', '#3aa0ff', '#ffb03a', '#7b5cff'],
            stroke: { color: '#ffffff', widthPx: 12 },
            shadow: { color: 'rgba(0,0,0,0.28)', blurPx: 0, offsetXPx: 0, offsetYPx: 6 },
            background: { mode: 'none', radiusPx: 0, paddingXPx: 0, paddingYPx: 0 },
            revealMode: 'word',
            wordEntrance: anim('bounce', 320, 'spring', 1.1),
            activeWord: { color: '#ffffff', scale: 1.08, karaoke: false, animation: anim('jelly', 540, 'linear', 1) },
            emphasis: { color: '#ffffff', scale: 1.2, bold: true, newLine: false },
            entrance: anim('none', 0),
            exit: anim('fade', 120),
        },
        placement: { anchor: 'lower', xNorm: 0.5, yNorm: 0, maxWidthNorm: 0.9 },
        segmentation: { maxWords: 4, preferredWords: 3, maxChars: 22, maxLines: 2, pauseThresholdMs: 440, breakOnSentencePunctuation: true },
    }),
];
export const TEMPLATES_BY_ID = Object.fromEntries(TEMPLATES.map((t) => [t.id, t]));
export function getTemplate(id) {
    return TEMPLATES_BY_ID[id];
}
export const DEFAULT_TEMPLATE_ID = 'bold-pop-1';
//# sourceMappingURL=templates.js.map